import React, { useContext } from 'react';
import * as THREE from 'three';
import { Divider, Switch } from 'antd';
import { BrandingContext } from 'pages/_app';
import { ThemeContext } from 'pages/_app';
import { EditorButton } from '@src/components/scene/canvas-html/editorButton';
import {
  UndoIcon,
  RedoIcon,
  MoveIcon,
  RotateIcon,
  ScaleIcon,
  DeleteIcon,
  DuplicateIcon,
  LinkIcon,
  POIIcon,
  TextureIcon,
  LightIcon,
  ShaderIcon,
  SnapToGridIcon,
} from '@src/components/scene/canvas-html/editorIcons';
import { ASSET_TYPE } from '@src/components/scene/react-three-components/sceneAsset';
import { EDITOR_MODE } from '@src/components/scene/react-three-components/sceneEditor';
import { makeId } from 'util/helpers';
import { openNotification } from 'util/helpers';
import { SceneAsset } from '@src/components/scene/react-three-components/sceneAsset';
import styles from './editor-buttons-group.module.css';

export default function EditorButtonGroup({
  undo,
  redo,
  isTransformControlsActive,
  isDisplayMinimapEnabled,
  setEditorMode,
  deleteSelectedModel,
  setIsTransformControlsActive,
  setIsEditorDrawerOpen,
  setIsMaterialDrawerOpen,
  selectedModel,
  setSelectedModel,
  setSelectableAssets,
  setIsLinkModalOpen,
  setIsPOIModalOpen,
  setIsPortalModalOpen,
  isTimelineMode,
  isLightAsset,
  setIsLightDrawerOpen,
  setIsShaderDrawerOpen,
  assets,
  editorMode,
}) {
  const branding = useContext(BrandingContext);
  const { isDark } = useContext(ThemeContext);

  const isNPCAvatar =
    selectedModel?.props?.asset?.type === ASSET_TYPE.NPC_AVATARS;

  function handleMinimapVisibilityChange(checked: boolean) {
    if (selectedModel?.props?.asset) {
      selectedModel.props.asset.showOnMinimap = checked;
      setSelectedModel({ ...selectedModel });
    }
  }

  function getMinimapVisibility(): boolean {
    return selectedModel?.props.asset?.showOnMinimap || false;
  }

  function snapToGridItem(): void {
    const threeObj = selectedModel.props.asset.ref.current;
    const newLocalPos = new THREE.Vector3(
      threeObj.position.x,
      0,
      threeObj.position.z
    );
    threeObj.position.copy(newLocalPos);
  }

  return (
    <div
      className={`${styles.buttonsWrapper} ${isDark ? styles.darkTheme : ''}`}
    >
      {!isTimelineMode && (
        <div className={styles.undoRedoButtonsWrapper}>
          <EditorButton toolTipMessage='Undo' key='undo' callback={undo}>
            <UndoIcon />
          </EditorButton>
          <EditorButton toolTipMessage='Redo' key='redo' callback={redo}>
            <RedoIcon />
          </EditorButton>
        </div>
      )}
      {isTransformControlsActive && (
        <div className={styles.editorButtonsWrapper}>
          <Divider type='vertical' className={styles.divider} />
          <>
            <EditorButton
              toolTipMessage='Move'
              key='move'
              callback={() => setEditorMode(EDITOR_MODE.TRANSLATE)}
              className={editorMode === EDITOR_MODE.TRANSLATE ? 'active' : ''}
            >
              <MoveIcon />
            </EditorButton>
            <EditorButton
              toolTipMessage='Rotate'
              key='rotate'
              callback={() => setEditorMode(EDITOR_MODE.ROTATE)}
              className={editorMode === EDITOR_MODE.ROTATE ? 'active' : ''}
            >
              <RotateIcon />
            </EditorButton>
            <EditorButton
              toolTipMessage='Scale'
              key='scale'
              disabled={isLightAsset}
              callback={() => setEditorMode(EDITOR_MODE.SCALE)}
              className={editorMode === EDITOR_MODE.SCALE ? 'active' : ''}
            >
              <ScaleIcon />
            </EditorButton>
          </>
          <EditorButton
            toolTipMessage='Delete'
            key='delete'
            callback={() => {
              deleteSelectedModel();
              setIsTransformControlsActive(() => false);
              setIsEditorDrawerOpen(() => false);
            }}
          >
            <DeleteIcon />
          </EditorButton>
          <EditorButton
            toolTipMessage='Duplicate'
            key='duplicate'
            callback={() => {
              if (!selectedModel) return;
              const threeObj = selectedModel.props.asset.ref.current;
              const instanceId = makeId(10);
              const currentLight = selectedModel.props?.light?.ref?.current;

              const assetType = {
                type: selectedModel.props.asset.type,
                url: selectedModel.props.asset.url,
                name: `${selectedModel.props.asset.name}-duplicate`,
                previewImage:
                  selectedModel.props.asset.previewImage ||
                  selectedModel.props.asset.url ||
                  '',
                id: '',
                ref: React.createRef(),
              };

              const sceneAsset = (
                <SceneAsset
                  key={`Scene-Asset-${instanceId}`}
                  position={
                    new THREE.Vector3(
                      threeObj.position.x + 3,
                      threeObj.position.y,
                      threeObj.position.z
                    )
                  }
                  scale={threeObj.scale || new THREE.Vector3(1, 1, 1)}
                  rotation={threeObj.rotation || new THREE.Euler(0, 0, 0)}
                  instanceId={instanceId}
                  asset={assetType}
                  {...(selectedModel.props.light && {
                    light: {
                      ...selectedModel.props.light,
                      ref: React.createRef(),
                      angle: currentLight.angle || null,
                      color: currentLight.color,
                      decay: currentLight.decay || null,
                      distance: currentLight.distance || null,
                      groundColor: currentLight.groundColor || null,
                      intensity: currentLight.intensity || 1,
                      penumbra: currentLight.penumbra || null,
                      shadow: currentLight.shadow || null,
                      skyColor: currentLight.skyColor || null,
                    },
                  })}
                />
              );
              setSelectedModel(null);
              setIsTransformControlsActive(false);
              setIsEditorDrawerOpen(() => false);
              setSelectableAssets({
                type: 'add',
                newElement: { ...sceneAsset },
              });
            }}
          >
            <DuplicateIcon />
          </EditorButton>
          {branding !== 'ZEPETO' && (
            <EditorButton
              toolTipMessage='Link asset'
              key='link'
              callback={() => {
                if (!selectedModel?.props.asset.id) {
                  openNotification(
                    'Link asset',
                    'Please save the scene to be able link this asset'
                  );
                } else {
                  setIsLinkModalOpen(true);
                }
              }}
            >
              <LinkIcon />
            </EditorButton>
          )}
          {branding !== 'ZEPETO' && (
            <EditorButton
              toolTipMessage='Set as POI'
              key='poi'
              callback={() => {
                if (!selectedModel?.props.asset.id) {
                  openNotification(
                    'Set as POI',
                    'Please save the scene to be able to set as Point of Interest'
                  );
                } else {
                  setIsPOIModalOpen(true);
                }
              }}
            >
              <POIIcon />
            </EditorButton>
          )}
          {branding !== 'ZEPETO' &&
            !selectedModel?.props.asset.isPortal &&
            selectedModel?.props.asset.type === ASSET_TYPE.GLTF.OTHER && (
              <EditorButton
                toolTipMessage='Change Texture'
                key='changeTexture'
                callback={() => {
                  setIsMaterialDrawerOpen(() => true);
                }}
              >
                <TextureIcon />
              </EditorButton>
            )}
          {selectedModel?.props.asset.type === ASSET_TYPE.MISC.LIGHT && (
            <EditorButton
              toolTipMessage='Light Settings'
              key='lightSettings'
              callback={() => {
                setIsLightDrawerOpen(() => true);
              }}
            >
              <LightIcon />
            </EditorButton>
          )}
          {selectedModel?.props.asset.type === ASSET_TYPE.SHADER.WATER && (
            <EditorButton
              toolTipMessage='Shader Settings'
              key='shaderSettings'
              callback={() => {
                setIsShaderDrawerOpen(() => true);
              }}
            >
              <ShaderIcon />
            </EditorButton>
          )}
          <EditorButton
            toolTipMessage='Snap to Grid'
            key='snapToGrid'
            callback={snapToGridItem}
          >
            <SnapToGridIcon />
          </EditorButton>
        </div>
      )}
      {isTransformControlsActive && isDisplayMinimapEnabled && (
        <div className={styles.minimapCheckboxWrapper}>
          <Divider type='vertical' className={styles.divider} />
          <span className={styles.minimapLabel}>Show on MINIMAP</span>
          <Switch
            checked={getMinimapVisibility()}
            onChange={handleMinimapVisibilityChange}
            size='small'
          />
        </div>
      )}
    </div>
  );
}
